/**
 * IMPORTS
 */

import { useCallback, useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import debounce from "lodash.debounce";
import { Eye, EyeOff, House, IdCard, Package, Plus, PlusCircle, User, X } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@components/button/button";
import { MaskedInputCep } from "@components/masked-input-cep";
import { MaskedInputCpfCnpj } from "@components/masked-input-cpf-cnpj";
import { MaskedInputPhone } from "@components/masked-input-phone";
import { SelectBaseAsync } from "@components/react-select-async";
import { SpinnerSimple } from "@components/spinner-simple/spinner-simple";

import {
  IConsultorPros,
  ICreateActvityModalProps,
} from "@pages/admin/gerenciar-clientes/components/create-client-modal/interface";
import {
  getFormSchema,
  initialValues,
} from "@pages/admin/gerenciar-clientes/components/create-client-modal/schema/schema-custumers";

import { useBuscarTiposEntrega } from "@/hooks/tipos-entregas/buscar-tipos-entrega";
import { useLoadConsultoresPaginate } from "@/hooks/usuários/buscar-usuario-consultor-pagination";
import { usePostCreateClienteCompleto } from "@/hooks/usuários/post-create-client-complet";

import type { ICreateClientPayload } from "@/data/dtos/users/post-create-client-complet-dto";
import { handleFormatTypeDelivery } from "@/utils/format-type-delivery";
import { notifyAlert } from "@/utils/notify-messages";

const removerFormatacao = (telefone: string) => {
  return telefone.replace(/\D/g, "");
};

const SUBODNARDO_ROLE_ID = import.meta.env.VITE_SUBODNARDO_ROLE_ID;
const CLIENTE_ROLE_ID = import.meta.env.VITE_CLIENTE_ROLE_ID;

const CreateClientModal = ({ handleToggleCreateActvityModal }: ICreateActvityModalProps) => {
  const loadConsultores = useLoadConsultoresPaginate();
  const [consultor, setConsultor] = useState<IConsultorPros | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Configurando o useForm com a validação do Zod
  const formSchema = useMemo(() => getFormSchema(), []);

  type FormData = z.infer<ReturnType<typeof getFormSchema>>;

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues,
  });
  const { criarClienteCompletoAsync, isLoading: isSalvandoClienteCompleto } =
    usePostCreateClienteCompleto();

  const { tiposEntrega } = useBuscarTiposEntrega();

  const [contatos, setContatos] = useState<
    {
      nome: string;
      email: string;
      telefone: string;
      documento: string;
      senha: string;
    }[]
  >([]);

  const handleRemoveUsuarioContato = useCallback(
    (email: string) => {
      const result = contatos?.filter((item) => item.email !== email);

      setContatos(result);
    },
    [contatos]
  );

  // Função para adicionar um novo contato ao array
  const handleAddContato = useCallback(() => {
    const novoContato = {
      nome: watch("usuarioCliente.nome_identificacao"),
      email: watch("usuarioCliente.email"),
      telefone: watch("usuarioCliente.telefone"),
      senha: watch("usuarioCliente.senha"),
      documento: watch("usuarioCliente.documento") as string,
    };

    // Verifica se todos os campos foram preenchidos
    if (
      !novoContato.nome ||
      !novoContato.email ||
      !novoContato.telefone ||
      !novoContato.senha ||
      !novoContato.documento
    ) {
      notifyAlert("Por favor, preencha todos os campos de contato.");
      return;
    }

    // Verifica se o e-mail já existe na lista de contatos
    const emailJaExiste = contatos.some(
      (contato) => contato.email.trim().toLowerCase() === novoContato.email.trim().toLowerCase()
    );

    if (emailJaExiste) {
      notifyAlert("Este e-mail já foi adicionado à lista de contatos.");
      return;
    }

    // Adiciona novo contato
    setContatos((prev) => [...prev, novoContato]);

    // Limpe os campos após adicionar o contato
    setValue("usuarioCliente.nome_identificacao", novoContato.nome);
    setValue("usuarioCliente.email", novoContato.email);
    setValue("usuarioCliente.telefone", novoContato.telefone);
    setValue("usuarioCliente.documento", novoContato.documento);
    setValue("usuarioCliente.senha", novoContato.senha);
  }, [setValue, watch, contatos]);

  function formatCep(cep: string) {
    // Remove todos os caracteres que não são números
    cep = cep.replace(/\D/g, "");

    // Aplica a máscara XXXXX-XXX
    return cep.replace(/^(\d{5})(\d{3})$/, "$1-$2");
  }

  // Função de busca no ViaCEP com debounce para evitar requisições excessivas
  const fetchAddress = debounce(async (cep: string) => {
    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
      const { logradouro, bairro, localidade, uf } = response.data;

      if (logradouro) {
        setValue("endereco.cidade", localidade);
        setValue("endereco.estado", uf);
        setValue("endereco.bairro", bairro);
        setValue("endereco.cep", formatCep(cep));
        setValue("endereco.logradouro", logradouro);

        // Disparando a validação para os campos preenchidos automaticamente
        await trigger([
          "endereco.cidade",
          "endereco.estado",
          "endereco.bairro",
          "endereco.cep",
          "endereco.logradouro",
        ]);
      }
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
    }
  }, 500);

  // Atualiza o CEP e faz a busca quando o campo é preenchido
  const handleCepChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const cepValue = event.target.value.replace(/\D/g, "");

    if (cepValue.length === 8) {
      await fetchAddress(cepValue);
    }
  };

  // Função para lidar com o envio do formulário (POST agregado `users/cliente-completo`)
  const onSubmit = useCallback(
    async (data: FormData) => {
      const entregaSelecionada = tiposEntrega.find(
        (entrega) => entrega.tipo === data.cliente.tipoEntrega?.toLocaleLowerCase()
      );

      const roleCliente = CLIENTE_ROLE_ID ? Number(CLIENTE_ROLE_ID) : null;
      const roleSub = SUBODNARDO_ROLE_ID ? Number(SUBODNARDO_ROLE_ID) : null;

      const usuariosClientePayload = contatos?.length
        ? contatos.map((c) => ({
            nome: c.nome,
            email: c.email.trim(),
            documento: removerFormatacao(c.documento),
            senha: c.senha,
            role_id: roleSub,
            ativo: true,
          }))
        : [
            {
              nome: data.usuarioCliente.nome_identificacao,
              email: data.usuarioCliente.email.trim(),
              documento: removerFormatacao(data.usuarioCliente.documento as string),
              senha: data.usuarioCliente.senha,
              role_id: roleSub,
              ativo: true,
            },
          ];

      const payload: ICreateClientPayload = {
        cliente: {
          nome: data.cliente.nome_identificacao,
          email: data.cliente.email.trim(),
          documento: removerFormatacao(data.cliente.documento),
          telefone: removerFormatacao(data.cliente.telefone_contato),
          tipo_pessoa: "J",
          senha: "cliente123",
          ativo: true,
          roles: roleCliente,
          consultor_id: consultor?.value,
          tipo_entrega_id: entregaSelecionada?.id,
        },
        endereco: {
          logradouro: data.endereco.logradouro ?? "Padrão",
          numero: data.endereco.numero,
          complemento: data.endereco.complemento ?? null,
          bairro: data.endereco.bairro,
          cidade: data.endereco.cidade,
          estado: data.endereco.estado,
          cep: data.endereco.cep.replace(/\D/g, ""),
          tipo_endereco: "residencial",
          nome_responsavel: data.deliveryAddress.name_address_delivery,
          email: data.deliveryAddress.email_address_delivery.trim(),
          setor: data.deliveryAddress.setor_address_delivery,
          telefone: removerFormatacao(data.deliveryAddress.phone_address_delivery),
        },
        usuarios_cliente: usuariosClientePayload,
      };

      try {
        await criarClienteCompletoAsync(payload);
        setConsultor(null);
        setContatos([]);
        handleToggleCreateActvityModal();
      } catch {
        // erros e toasts tratados em `usePostCreateClienteCompleto`
      }
    },
    [contatos, criarClienteCompletoAsync, consultor, tiposEntrega, handleToggleCreateActvityModal]
  );

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/60">
      <div className="h-[80vh] w-[80vw] space-y-5 overflow-y-auto rounded-xl bg-neutral-50 px-6 py-5 shadow-shape">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center justify-center gap-2">
              <User className="text-zinc-800" size={24} />
              <h2 className="text-xl font-semibold text-zinc-800">Cadastro de Cliente</h2>
            </div>
            <button onClick={handleToggleCreateActvityModal}>
              <X className="size-5 text-zinc-400" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          {/* Seção de endereço de entrega */}

          <div className="mt-2 flex-row items-center rounded-md bg-neutral-50">
            <SelectBaseAsync
              label="Consultor"
              loadOptions={loadConsultores as any}
              onChange={(option: any) => setConsultor(option)}
            />
          </div>

          {/* Informações do Cliente */}
          <div className="mb-6 grid grid-cols-2 gap-4">
            {/* NOME DE IDENTIFICACAO */}
            <div className="flex-col">
              <p className="mb-2 text-zinc-800">Nome da Empresa</p>
              <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                <IdCard className="size-5 text-zinc-400" />
                <input
                  {...register("cliente.nome_identificacao")}
                  placeholder="Informe o nome da Empresa"
                  className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                />
              </div>
              {errors.cliente?.nome_identificacao && (
                <span className="text-sm text-red-500">
                  {errors.cliente.nome_identificacao.message}
                </span>
              )}
            </div>

            {/* NOME DA PESSOA PARA CONTATO */}
            <div className="flex-col">
              <p className="mb-2 text-zinc-800">Responsável</p>
              <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                <IdCard className="size-5 text-zinc-400" />
                <input
                  {...register("cliente.nome_contato")}
                  placeholder="Informe o nome do Responsável"
                  className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                />
              </div>
              {errors.cliente?.nome_contato && (
                <span className="text-sm text-red-500">{errors.cliente.nome_contato.message}</span>
              )}
            </div>

            {/* CNPJ/CPF */}
            <div className="flex-col">
              <p className="mb-2 text-zinc-800">CNPJ/CPF</p>
              <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                <IdCard className="size-5 text-zinc-400" />

                <MaskedInputCpfCnpj
                  {...register("cliente.documento")}
                  placeholder="Digite CPF ou CNPJ"
                  className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                />
              </div>
              {errors.cliente?.documento && (
                <span className="text-sm text-red-500">{errors.cliente.documento.message}</span>
              )}
            </div>

            {/* E-MAIL */}
            <div className="flex-col">
              <p className="mb-2 text-zinc-800">E-mail</p>
              <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                <IdCard className="size-5 text-zinc-400" />
                <input
                  {...register("cliente.email")}
                  placeholder="Informe o e-mail do Responsável"
                  className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                />
              </div>
              {errors.cliente?.email && (
                <span className="text-sm text-red-500">{errors.cliente.email.message}</span>
              )}
            </div>
          </div>

          {/* TELEFONE DE CONTATO */}
          <div className="w-ful">
            <p className="mb-2 text-zinc-800">Telefone do Responsável</p>
            <div className="flex h-14 w-full items-center gap-2 rounded-lg border border-zinc-800 px-4">
              <IdCard className="size-5 text-zinc-400" />

              <MaskedInputPhone
                {...register("cliente.telefone_contato")}
                placeholder="Informe o Telefone do Responsável"
                className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
              />
            </div>
            {errors.cliente?.telefone_contato && (
              <span className="text-sm text-red-500">
                {errors.cliente.telefone_contato.message}
              </span>
            )}
          </div>

          {/**SEPARADOR */}
          <div className="h-4 w-full" />

          {/* Seção de Endereço */}
          <div className="w-ful">
            <div className="mb-4 mt-4 flex w-full items-center justify-start gap-2">
              <House className="text-zinc-800" size={24} />
              <h2 className="text-xl font-semibold text-zinc-800">Endereço da Empresa</h2>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex-col">
                <p className="mb-2 text-zinc-800">CEP</p>
                <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                  <IdCard className="size-5 text-zinc-400" />

                  <MaskedInputCep
                    {...register("endereco.cep")}
                    onChange={handleCepChange}
                    placeholder="Informe o CEP"
                    className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                  />
                </div>
                {errors.endereco?.cep && (
                  <span className="text-sm text-red-500">{errors.endereco.cep.message}</span>
                )}
              </div>

              <div className="flex-col">
                <p className="mb-2 text-zinc-800">Estado</p>
                <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                  <IdCard className="size-5 text-zinc-400" />
                  <input
                    {...register("endereco.estado")}
                    placeholder="Informe o Estado"
                    className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                  />
                </div>
                {errors.endereco?.estado && (
                  <span className="text-sm text-red-500">{errors.endereco.estado.message}</span>
                )}
              </div>
            </div>
          </div>

          {/* LOGRADOURO + NÚMERO + COMPLEMENTO — SOBEM */}
          <div className="mt-4 grid w-full grid-cols-12 gap-4">
            {/* Logradouro */}
            <div className="col-span-6 flex-col">
              <p className="mb-2 text-zinc-800">Logradouro</p>
              <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                <IdCard className="size-5 text-zinc-400" />
                <input
                  {...register("endereco.logradouro")}
                  placeholder="Rua / Avenida"
                  className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                />
              </div>
            </div>

            {/* Número */}
            <div className="col-span-2 flex-col">
              <p className="mb-2 text-zinc-800">Número</p>
              <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                <IdCard className="size-5 text-zinc-400" />
                <input
                  {...register("endereco.numero")}
                  placeholder="Nº"
                  className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                />
              </div>
            </div>

            {/* Complemento */}
            <div className="col-span-4 flex-col">
              <p className="mb-2 text-zinc-800">Complemento</p>
              <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                <IdCard className="size-5 text-zinc-400" />
                <input
                  {...register("endereco.complemento")}
                  placeholder="Bloco / apto / sala"
                  className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                />
              </div>
            </div>
          </div>

          {/* CIDADE + BAIRRO  */}
          <div className="mt-4 grid grid-cols-2 gap-4">
            {/* Cidade */}
            <div className="flex-col">
              <p className="mb-2 text-zinc-800">Cidade</p>
              <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                <IdCard className="size-5 text-zinc-400" />
                <input
                  {...register("endereco.cidade")}
                  placeholder="Informe a Cidade"
                  className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                />
              </div>
            </div>

            {/* Bairro */}
            <div className="flex-col">
              <p className="mb-2 text-zinc-800">Bairro</p>
              <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                <IdCard className="size-5 text-zinc-400" />
                <input
                  {...register("endereco.bairro")}
                  placeholder="Informe o Bairro"
                  className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                />
              </div>
            </div>
          </div>

          {/* SEÇÃO PARA ADICIONAR UM USUÁRIO PARA UM CLIENTE */}
          <div className="flex w-full items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <User className="text-zinc-800" size={24} />
              <h2 className="text-xl font-semibold text-zinc-800">Usuários solicitantes</h2>
            </div>

            <Button type="button" size="sm" className="h-8 gap-1" onClick={handleAddContato}>
              <PlusCircle className="h-3.5 w-3.5" />
              <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">Adiconar usuário</span>
            </Button>
          </div>

          {/* CONTATOS PARA CLIENTE */}
          <div className="mb-2 flex flex-wrap gap-2">
            {contatos?.map((conctact) => (
              <div
                key={String(conctact.nome)}
                className="flex items-center gap-2 rounded-md bg-zinc-800 px-2.5 py-1.5"
              >
                <span className="text-zinc-300">{conctact.email}</span>

                <button
                  onClick={() => {
                    handleRemoveUsuarioContato(conctact.email);
                  }}
                >
                  <X className="size-4 text-zinc-400" />
                </button>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* NOME DE IDENTIFICACAO */}
            <div className="flex-col">
              <p className="mb-2 text-zinc-800">Nome do Usuário</p>
              <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                <IdCard className="size-5 text-zinc-400" />
                <input
                  {...register("usuarioCliente.nome_identificacao")}
                  placeholder="Informe o Nome do Usuário"
                  className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                />
              </div>
              {errors.usuarioCliente?.nome_identificacao && (
                <span className="text-sm text-red-500">
                  {errors.usuarioCliente?.nome_identificacao.message}
                </span>
              )}
            </div>

            {/* E-MAIL */}
            <div className="flex-col">
              <p className="mb-2 text-zinc-800">E-mail</p>
              <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                <IdCard className="size-5 text-zinc-400" />
                <input
                  {...register("usuarioCliente.email")}
                  placeholder="Informe o E-mail de Usuário"
                  className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                />
              </div>
              {errors.usuarioCliente?.email && (
                <span className="text-sm text-red-500">{errors.usuarioCliente.email.message}</span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* CPF/CNPJ DE IDENTIFICACAO */}
            <div className="flex-col">
              <p className="mb-2 text-zinc-800">Documento</p>
              <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                <IdCard className="size-5 text-zinc-400" />

                <MaskedInputCpfCnpj
                  {...register("usuarioCliente.documento")}
                  placeholder="Digite CPF ou CNPJ"
                  className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                />
              </div>
              {errors.usuarioCliente?.documento && (
                <span className="text-sm text-red-500">
                  {errors.usuarioCliente?.documento.message}
                </span>
              )}
            </div>

            {/* SENHA */}
            <div className="flex-col">
              <p className="mb-2 text-zinc-800">Senha</p>

              <div className="relative flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                <IdCard className="size-5 text-zinc-400" />

                <input
                  type={showPassword ? "text" : "password"}
                  {...register("usuarioCliente.senha")}
                  placeholder="Informe uma senha"
                  className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 text-zinc-500 hover:text-zinc-700"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              {errors.usuarioCliente?.senha && (
                <span className="text-sm text-red-500">{errors.usuarioCliente.senha.message}</span>
              )}
            </div>
          </div>

          {/* TELEFONE DE CONTATO */}
          <div className="w-ful">
            <p className="mb-2 text-zinc-800">Telefone do Usuário</p>
            <div className="flex h-14 w-full items-center gap-2 rounded-lg border border-zinc-800 px-4">
              <IdCard className="size-5 text-zinc-400" />

              <MaskedInputPhone
                {...register("usuarioCliente.telefone")}
                placeholder="Informe o Telefone do Responsável"
                className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
              />
            </div>
            {errors.usuarioCliente?.telefone && (
              <span className="text-sm text-red-500">{errors.usuarioCliente.telefone.message}</span>
            )}
          </div>

          {/* FIM SEÇÃO PARA ADICIONAR UM USUÁRIO PARA UM CLIENTE */}
          {/**SEPARADOR */}
          <div className="h-4 w-full" />

          {/* Tipo de entrega */}
          <div>
            <div className="mb-4 flex">
              <Package className="text-zinc-800" size={24} />
              <p className="ml-2 text-zinc-800">Tipo de entrega</p>
            </div>
            <select
              {...register("cliente.tipoEntrega")}
              className="h-12 w-full rounded-lg border border-zinc-800 bg-neutral-50 px-4 text-zinc-800 outline-none"
            >
              <option value="">Selecione um tipo</option>
              {tiposEntrega?.map((tipo) => (
                <option key={tipo.id} value={tipo.tipo}>
                  {handleFormatTypeDelivery(tipo.tipo)}
                </option>
              ))}
            </select>

            {errors.cliente?.tipoEntrega && (
              <p className="text-red-500">{errors.cliente.tipoEntrega.message}</p>
            )}
          </div>

          {/**SEPARADOR */}
          <div className="h-4 w-full" />

          <div className="w-full">
            <div className="mb-4 grid grid-cols-2 gap-4">
              {/* NOME DE IDENTIFICACAO */}
              <div className="flex-col">
                <p className="mb-2 text-zinc-800">Nome do Responsável pelo recebimento</p>
                <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                  <IdCard className="size-5 text-zinc-400" />
                  <input
                    {...register("deliveryAddress.name_address_delivery")}
                    placeholder="Informe o Nome do Responsável"
                    className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                  />
                </div>
                {errors.deliveryAddress?.name_address_delivery && (
                  <span className="text-sm text-red-500">
                    {errors.deliveryAddress?.name_address_delivery.message}
                  </span>
                )}
              </div>
              {/* E-MAIL */}
              <div className="flex-col">
                <p className="mb-2 text-zinc-800">E-mail</p>
                <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                  <IdCard className="size-5 text-zinc-400" />
                  <input
                    {...register("deliveryAddress.email_address_delivery")}
                    placeholder="Informe o E-mail do Responsável"
                    className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                  />
                </div>
                {errors.deliveryAddress?.email_address_delivery && (
                  <span className="text-sm text-red-500">
                    {errors.deliveryAddress.email_address_delivery?.message}
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* SETOR */}
              <div className="flex-col">
                <p className="mb-2 text-zinc-800">Setor</p>
                <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                  <IdCard className="size-5 text-zinc-400" />
                  <input
                    {...register("deliveryAddress.setor_address_delivery")}
                    placeholder="Informe o Setor do Responsável"
                    className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                  />
                </div>
                {errors.deliveryAddress?.setor_address_delivery && (
                  <span className="text-sm text-red-500">
                    {errors.deliveryAddress?.setor_address_delivery.message}
                  </span>
                )}
              </div>

              {/* TELEFONE */}
              <div className="flex-col">
                <p className="mb-2 text-zinc-800">Telefone</p>
                <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                  <IdCard className="size-5 text-zinc-400" />

                  <MaskedInputPhone
                    {...register("deliveryAddress.phone_address_delivery")}
                    placeholder="Informe o Telefone do Responsável"
                    className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                  />
                </div>
                {errors.deliveryAddress?.phone_address_delivery && (
                  <span className="text-sm text-red-500">
                    {errors.deliveryAddress?.phone_address_delivery.message}
                  </span>
                )}
              </div>
            </div>
          </div>

          <Button
            disabled={isSalvandoClienteCompleto}
            type="submit"
            className="mt-6 w-full ring-offset-background"
          >
            {isSalvandoClienteCompleto ? (
              <SpinnerSimple />
            ) : (
              <>
                <Plus className="size-5" />
                Salvar
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

/**
 * EXPORT
 */
export { CreateClientModal };
